import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled, { keyframes } from 'styled-components';
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
`;
const HeroSection = styled.section `
  padding: 100px 24px 60px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);

  @media (max-width: 768px) {
    padding: 70px 16px 40px;
  }
`;
const HeroContent = styled.div `
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeUp} 0.8s ease-out;
`;
const PageTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 400;
  color: var(--text-primary);
  margin-bottom: 16px;
  letter-spacing: 0.02em;
`;
const LastUpdated = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-tertiary);
  font-style: italic;
`;
const ContentWrapper = styled.main `
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  animation: ${fadeUp} 0.8s ease-out 0.2s backwards;

  @media (max-width: 768px) {
    padding: 32px 16px 60px;
  }
`;
const Section = styled.section `
  margin-bottom: 40px;
`;
const SectionTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 22px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 60px;
    height: 2px;
    background: var(--accent-gold, #B8860B);
  }
`;
const SubsectionTitle = styled.h3 `
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-top: 24px;
  margin-bottom: 12px;
`;
const Paragraph = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 16px;
`;
const LegalNotice = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-tertiary);
  line-height: 1.8;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;
const List = styled.ul `
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
`;
const ListItem = styled.li `
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding: 8px 0 8px 28px;
  position: relative;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 16px;
    width: 8px;
    height: 8px;
    background: var(--accent-gold, #B8860B);
    opacity: 0.6;
  }

  strong {
    color: var(--text-primary);
  }
`;
const Link = styled.a `
  color: var(--accent-gold, #B8860B);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    text-decoration: underline;
  }
`;
const ContactCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 24px;
  margin-top: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: var(--accent-gold, #B8860B);
  }
`;
const ContactText = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 0;

  strong {
    color: var(--text-primary);
    display: block;
    margin-bottom: 8px;
  }
`;
const NoticeBox = styled.div `
  background: var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  border: 1px solid var(--accent-gold, #B8860B);
  padding: 20px 24px;
  margin-top: 24px;

  strong {
    color: var(--accent-gold, #B8860B);
    display: block;
    margin-bottom: 8px;
  }

  p {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.7;
    margin: 0;
  }
`;
const TermsOfService = () => {
    return (_jsxs(PageWrapper, { children: [_jsx(HeroSection, { children: _jsxs(HeroContent, { children: [_jsx(PageTitle, { children: "Terms of Service" }), _jsx(LastUpdated, { children: "Last Updated: October 28, 2025" })] }) }), _jsxs(ContentWrapper, { children: [_jsxs(Section, { children: [_jsx(SectionTitle, { children: "1. Acceptance of Terms" }), _jsx(Paragraph, { children: "By accessing and using the Royal Gems, Inc. platform (\"Service\"), you accept and agree to be bound by these Terms of Service (\"Terms\"). If you do not agree to these Terms, please do not use our Service." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "2. Description of Service" }), _jsx(Paragraph, { children: "Royal Gems, Inc. operates a secure business-to-business diamond trading platform that provides:" }), _jsxs(List, { children: [_jsx(ListItem, { children: "Authenticated access for verified diamond dealers" }), _jsx(ListItem, { children: "Diamond inventory browsing and search capabilities" }), _jsx(ListItem, { children: "Memo request management system" }), _jsx(ListItem, { children: "Diamond comparison and analysis tools" }), _jsx(ListItem, { children: "Certificate verification services" }), _jsx(ListItem, { children: "Dealer analytics and performance tracking" })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "3. Account Registration and Eligibility" }), _jsx(SubsectionTitle, { children: "3.1 Eligibility" }), _jsx(Paragraph, { children: "You must:" }), _jsxs(List, { children: [_jsx(ListItem, { children: "Be at least 18 years of age" }), _jsx(ListItem, { children: "Represent a legitimate diamond trading business" }), _jsx(ListItem, { children: "Provide accurate and complete business information" }), _jsx(ListItem, { children: "Have authority to bind your business to these Terms" })] }), _jsx(SubsectionTitle, { children: "3.2 Account Security" }), _jsx(Paragraph, { children: "You are responsible for:" }), _jsxs(List, { children: [_jsx(ListItem, { children: "Maintaining the confidentiality of your account credentials" }), _jsx(ListItem, { children: "All activities that occur under your account" }), _jsx(ListItem, { children: "Notifying us immediately of any unauthorized access" }), _jsx(ListItem, { children: "Ensuring your account information remains current and accurate" })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "4. Acceptable Use" }), _jsx(SubsectionTitle, { children: "4.1 Permitted Use" }), _jsx(Paragraph, { children: "You may use our Service only for legitimate business purposes related to diamond trading." }), _jsx(SubsectionTitle, { children: "4.2 Prohibited Activities" }), _jsx(Paragraph, { children: "You may not:" }), _jsxs(List, { children: [_jsx(ListItem, { children: "Use the Service for any illegal purposes" }), _jsx(ListItem, { children: "Attempt to gain unauthorized access to the platform or other accounts" }), _jsx(ListItem, { children: "Interfere with or disrupt the Service" }), _jsx(ListItem, { children: "Use automated tools to scrape or extract data" }), _jsx(ListItem, { children: "Misrepresent your identity or business credentials" }), _jsx(ListItem, { children: "Share your account credentials with unauthorized parties" }), _jsx(ListItem, { children: "Reverse engineer or attempt to extract source code" })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "5. Memo Request Terms" }), _jsx(SubsectionTitle, { children: "5.1 Memo Process" }), _jsx(Paragraph, { children: "Memo requests are subject to approval by Royal Gems, Inc. Approval is at our sole discretion and based on factors including dealer standing, credit history, and diamond availability." }), _jsx(SubsectionTitle, { children: "5.2 Memo Responsibilities" }), _jsx(Paragraph, { children: "When a memo is approved, you agree to:" }), _jsxs(List, { children: [_jsx(ListItem, { children: "Return diamonds by the specified due date" }), _jsx(ListItem, { children: "Maintain proper insurance coverage" }), _jsx(ListItem, { children: "Handle diamonds with professional care" }), _jsx(ListItem, { children: "Report any damage or loss immediately" }), _jsx(ListItem, { children: "Pay for any diamonds retained beyond the memo period" })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "6. Intellectual Property" }), _jsx(Paragraph, { children: "All content, features, and functionality of the Service are owned by Royal Gems, Inc. and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "7. Diamond Information and Certificates" }), _jsx(Paragraph, { children: "We provide links to third-party diamond certification laboratories (GIA, AGS, IGI, HRD, EGL USA, GCAL). While we strive for accuracy, we make no warranties about diamond information or certificate data. You are responsible for verifying all diamond specifications independently." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "8. Privacy and Data Protection" }), _jsxs(Paragraph, { children: ["Your use of the Service is also governed by our ", _jsx(Link, { href: "/privacy-policy", children: "Privacy Policy" }), ". By using the Service, you consent to our collection and use of personal information as described therein."] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "9. Payment Terms" }), _jsx(Paragraph, { children: "Payment terms for diamond purchases are established separately between Royal Gems, Inc. and verified dealers. All payments must be made according to agreed-upon terms. Late payments may result in account suspension or termination." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "10. Disclaimers" }), _jsx(LegalNotice, { children: "The Service is provided \"as is\" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, error-free, or completely secure." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "11. Limitation of Liability" }), _jsx(LegalNotice, { children: "To the maximum extent permitted by law, Royal Gems, Inc. shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "12. Indemnification" }), _jsx(Paragraph, { children: "You agree to indemnify and hold harmless Royal Gems, Inc. from any claims, damages, losses, liabilities, and expenses arising from your violation of these Terms or your use of the Service." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "13. Termination" }), _jsx(Paragraph, { children: "We reserve the right to suspend or terminate your account at any time for violations of these Terms, suspicious activity, or at our sole discretion. You may terminate your account by contacting us." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "14. Changes to Terms" }), _jsx(Paragraph, { children: "We may modify these Terms at any time. Material changes will be notified via email or platform notification. Continued use of the Service after changes constitutes acceptance of the new Terms." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "15. Governing Law" }), _jsx(Paragraph, { children: "These Terms are governed by the laws of the United States. Any disputes shall be resolved in the courts of the United States." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "16. Contact Information" }), _jsx(Paragraph, { children: "For questions about these Terms, please contact us:" }), _jsx(ContactCard, { children: _jsxs(ContactText, { children: [_jsx("strong", { children: "Royal Gems, Inc." }), "Email: ", _jsx(Link, { href: "mailto:legal@royalgems.com", children: "legal@royalgems.com" }), _jsx("br", {}), "Support: ", _jsx(Link, { href: "/contact", children: "Contact Page" })] }) })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "17. Severability" }), _jsx(Paragraph, { children: "If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "18. Entire Agreement" }), _jsx(Paragraph, { children: "These Terms, together with our Privacy Policy, constitute the entire agreement between you and Royal Gems, Inc. regarding the use of our Service." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "19. Legitimate Business Platform Notice" }), _jsxs(NoticeBox, { children: [_jsx("strong", { children: "Important" }), _jsx("p", { children: "Royal Gems, Inc. is a legitimate, secure business-to-business diamond trading platform. Our authentication requirements and information collection practices are standard for secure B2B commerce. We are committed to maintaining the highest standards of security, transparency, and professional service for our verified dealer network." })] })] })] })] }));
};
export default TermsOfService;
